import { Controller } from "@hotwired/stimulus"

const FORM_OPEN_EVENT = "crud:form-opened"
const originalMarkup = new Map()

export default class extends Controller {
  static targets = ["container"]
  static values = {
    isPersisted: { type: Boolean, default: true }
  }

  connect() {
    this.handleExternalOpenRef = this.handleExternalOpen.bind(this)
    window.addEventListener(FORM_OPEN_EVENT, this.handleExternalOpenRef)

    if (this.isPersistedValue) {
      this.originalHtml = originalMarkup.get(this.formIdentifier) || null
      this.editing = Boolean(this.element.querySelector('form.inline-edit-form'))

      if (!this.editing) {
        this.clearStoredHtml()
      }
    }
  }

  disconnect() {
    window.removeEventListener(FORM_OPEN_EVENT, this.handleExternalOpenRef)
  }

  toggle(event) {
    event.preventDefault()

    if (this.isPersistedValue) {
      this.editing ? this.restore() : this.openInline(event.currentTarget.href)
    } else {
      this.isVisible ? this.hide() : this.show()
    }
  }

  show(event) {
    if (event) event.preventDefault()
    if (this.isPersistedValue || !this.hasContainerTarget || this.isVisible) return

    this.dispatchFormOpened()
    this.containerTarget.classList.remove('tw-hidden')
    this.toggleEmptyState(true)
  }

  hide(event) {
    if (event) event.preventDefault()

    if (this.isPersistedValue) {
      this.restore()
    } else if (this.hasContainerTarget) {
      this.containerTarget.classList.add('tw-hidden')
      this.toggleEmptyState(false)
      this.resetNewForm()
    }
  }

  async openInline(url) {
    if (!url) return

    this.editing = true
    this.captureOriginalHtml()
    this.dispatchFormOpened()

    await window.turboStreamHandler.runRequest(url, "GET")
  }

  restore() {
    if (!this.originalHtml) return

    this.element.innerHTML = this.originalHtml
    this.editing = false
    this.clearStoredHtml()
    window.reinitializeTippy()
  }

  handleExternalOpen(event) {
    const { sourceId } = event.detail || {}
    if (!sourceId || sourceId === this.formIdentifier) return

    if (this.isPersistedValue && this.editing) {
      this.restore()
    } else if (!this.isPersistedValue && this.isVisible) {
      this.hide()
    }
  }

  dispatchFormOpened() {
    window.dispatchEvent(new CustomEvent(FORM_OPEN_EVENT, {
      detail: { sourceId: this.formIdentifier }
    }))
  }

  captureOriginalHtml() {
    if (this.originalHtml) return

    this.originalHtml = this.element.innerHTML
    originalMarkup.set(this.formIdentifier, this.originalHtml)
  }

  clearStoredHtml() {
    originalMarkup.delete(this.formIdentifier)
    this.originalHtml = null
  }

  get formIdentifier() {
    return this.element.id
  }

  get isVisible() {
    return this.hasContainerTarget && !this.containerTarget.classList.contains('tw-hidden')
  }

  toggleEmptyState(hide) {
    const root = this.element.closest("[data-crud-root]") || this.element
    if (!root) return

    const emptyState = root.querySelector("[data-crud-empty-state]")
    if (!emptyState) return

    if (hide) {
      emptyState.classList.toggle("tw-hidden", true)
      return
    }

    const list = root.querySelector("[data-crud-list]") || root
    const hasItems = !!(list.querySelector("[data-crud-item]") || root.querySelector("[data-crud-item]"))
    emptyState.classList.toggle("tw-hidden", hasItems)
  }

  resetNewForm() {
    if (!this.hasContainerTarget) return
    const form = this.containerTarget.querySelector("form")
    if (!form) return

    form.reset()

    form.querySelectorAll("[data-controller~='crud-attachments']").forEach((element) => {
      const controller = window.Stimulus?.getControllerForElementAndIdentifier(
        element,
        "crud-attachments"
      )
      if (controller?.reset) controller.reset()
    })
  }
}
